import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { toast } from '../hooks/use-toast';
import { moveoutAPI } from '../services/api';
import { 
  CheckCircle, 
  Clock, 
  Camera, 
  Upload, 
  X, 
  AlertTriangle, 
  Loader2,
  Image,
  Send,
  Calendar
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MoveOutChecklist = ({ lease }) => {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState('');

  const fetchChecklist = useCallback(async () => {
    try {
      setLoading(true);
      const response = await moveoutAPI.getChecklist();
      setChecklist(response.data);
      setNotes(response.data.notes || '');
    } catch (error) {
      console.error('Error fetching checklist:', error);
      // If 404, tenant may not have an active lease with scheduled termination
      if (error.response?.status === 404) {
        setChecklist(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  const handleCheckboxChange = async (itemId, checked) => {
    if (!checklist) return;

    const updatedItems = checklist.items.map(item => 
      item.item_id === itemId 
        ? { ...item, completed: checked, completed_at: checked ? new Date().toISOString() : null }
        : item
    );

    setChecklist({ ...checklist, items: updatedItems });

    // Auto-save
    try {
      await moveoutAPI.saveChecklist({
        checklist_items: updatedItems,
        notes: notes
      });
    } catch (error) {
      console.error('Error saving checklist:', error);
      toast({
        title: "Error",
        description: "Failed to save checklist",
        variant: "destructive"
      });
    }
  };

  const handleSaveNotes = async () => {
    if (!checklist) return;

    setSaving(true);
    try {
      await moveoutAPI.saveChecklist({
        checklist_items: checklist.items,
        notes: notes
      });
      toast({
        title: "Saved",
        description: "Notes saved successfully"
      });
    } catch (error) {
      console.error('Error saving notes:', error);
      toast({
        title: "Error",
        description: "Failed to save notes",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('photos', file);
      });

      const response = await moveoutAPI.uploadPhotos(formData);
      
      toast({
        title: "Photos Uploaded",
        description: `Successfully uploaded ${response.data.photos.length} photo(s)`
      });

      // Refresh checklist to get updated photos
      fetchChecklist();
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast({
        title: "Error",
        description: "Failed to upload photos",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    try {
      await moveoutAPI.deletePhoto(photoUrl);
      
      // Update local state
      setChecklist({
        ...checklist,
        photos: checklist.photos.filter(p => p.url !== photoUrl)
      });

      toast({
        title: "Photo Deleted",
        description: "Photo removed successfully"
      });
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast({
        title: "Error",
        description: "Failed to delete photo",
        variant: "destructive"
      });
    }
  };

  const handleSubmitChecklist = async () => {
    if (!checklist) return;

    // Validate
    const incompleteItems = checklist.items.filter(item => !item.completed);
    if (incompleteItems.length > 0) {
      toast({
        title: "Incomplete Checklist",
        description: "Please complete all checklist items before submitting",
        variant: "destructive"
      });
      return;
    }

    if (!checklist.photos || checklist.photos.length === 0) {
      toast({
        title: "Photos Required",
        description: "Please upload at least one photo of your clean room with furniture to submit",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);
    try {
      await moveoutAPI.submitChecklist();
      
      toast({
        title: "Checklist Submitted! ✅",
        description: "Your landlord will review your move-out checklist"
      });

      fetchChecklist();
    } catch (error) {
      console.error('Error submitting checklist:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to submit checklist",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!checklist) {
    return (
      <Card className="border-gray-200">
        <CardContent className="py-8">
          <div className="text-center text-gray-500">
            <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Move-out checklist will be available when your lease has a scheduled termination date.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const completedCount = checklist.items.filter(item => item.completed).length;
  const totalCount = checklist.items.length;
  const progress = Math.round((completedCount / totalCount) * 100);
  const isSubmitted = checklist.status === 'submitted';
  const isReviewed = checklist.reviewed_by_landlord;

  return (
    <Card className="border-orange-200">
      <CardHeader className="bg-orange-50 border-b border-orange-200">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-orange-800">
            <CheckCircle className="h-5 w-5" />
            Move-Out Checklist
          </CardTitle>
          <div className="flex items-center gap-2">
            {isSubmitted && (
              <Badge className={isReviewed ? (checklist.review_status === 'approved' ? 'bg-green-500' : 'bg-red-500') : 'bg-blue-500'}>
                {isReviewed ? (checklist.review_status === 'approved' ? 'Approved' : 'Needs Attention') : 'Under Review'}
              </Badge>
            )}
            {!isSubmitted && (
              <Badge variant="outline" className="text-orange-600 border-orange-300">
                {progress}% Complete
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-6">
        {/* Important Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
            <div>
              <p className="font-medium text-yellow-800">Important: Deposit Return Requirements</p>
              <p className="text-sm text-yellow-700 mt-1">
                To receive your security deposit back, you must complete all checklist items and upload photos 
                showing your clean room with all furniture in place before your move-out date.
              </p>
            </div>
          </div>
        </div>

        {/* Checklist Items */}
        <div className="space-y-3 mb-6">
          {checklist.items.map((item) => (
            <div 
              key={item.item_id} 
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                item.completed ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'
              }`}
            >
              <Checkbox
                id={item.item_id}
                checked={item.completed}
                onCheckedChange={(checked) => handleCheckboxChange(item.item_id, checked)}
                disabled={isSubmitted}
                className="mt-0.5"
              />
              <label 
                htmlFor={item.item_id}
                className={`flex-1 cursor-pointer ${item.completed ? 'text-green-700 line-through' : 'text-gray-700'}`}
              >
                {item.label}
              </label>
              {item.completed && (
                <CheckCircle className="h-5 w-5 text-green-500" />
              )}
            </div>
          ))}
        </div>

        {/* Photo Upload Section */}
        <div className="border-t pt-6">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            <Camera className="h-5 w-5 text-orange-600" />
            Move-Out Photos
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Upload photos of your clean room with all provided furniture still in place. 
            These photos are required to receive your deposit back.
          </p>

          {/* Photo Grid */}
          {checklist.photos && checklist.photos.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
              {checklist.photos.map((photo, index) => (
                <div key={index} className="relative group">
                  <img
                    src={photo.url.startsWith('http') ? photo.url : `${API_URL}${photo.url}`}
                    alt={`Move-out photo ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg border"
                  />
                  {!isSubmitted && (
                    <button
                      onClick={() => handleDeletePhoto(photo.url)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Upload Button */}
          {!isSubmitted && (
            <div className="flex items-center gap-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <div className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-orange-300 rounded-lg text-orange-600 hover:bg-orange-50 transition-colors">
                  {uploading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Upload className="h-5 w-5" />
                  )}
                  <span>{uploading ? 'Uploading...' : 'Upload Photos'}</span>
                </div>
              </label>
              {checklist.photos && checklist.photos.length > 0 && (
                <span className="text-sm text-gray-500">
                  {checklist.photos.length} photo(s) uploaded
                </span>
              )}
            </div>
          )}
        </div>

        {/* Notes Section */}
        <div className="border-t pt-6 mt-6">
          <h3 className="font-semibold text-gray-900 mb-2">Additional Notes</h3>
          <Textarea
            placeholder="Add any notes about the condition of the room or furniture..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitted}
            rows={3}
          />
          {!isSubmitted && (
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={handleSaveNotes}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save Notes
            </Button>
          )}
        </div>

        {/* Landlord Review Notes */}
        {isReviewed && checklist.landlord_notes && (
          <div className={`border-t pt-6 mt-6 p-4 rounded-lg ${
            checklist.review_status === 'approved' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
          }`}>
            <h3 className="font-semibold mb-2">
              Landlord Feedback
            </h3>
            <p className="text-sm">{checklist.landlord_notes}</p>
          </div>
        )}

        {/* Submit Button */}
        {!isSubmitted && (
          <div className="border-t pt-6 mt-6">
            <Button 
              className="w-full bg-orange-600 hover:bg-orange-700"
              onClick={handleSubmitChecklist}
              disabled={submitting || completedCount < totalCount || !checklist.photos || checklist.photos.length === 0}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Submit Move-Out Checklist
                </>
              )}
            </Button>
            {(completedCount < totalCount || !checklist.photos || checklist.photos.length === 0) && (
              <p className="text-xs text-center text-gray-500 mt-2">
                Complete all items and upload at least one photo to submit
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MoveOutChecklist;
